import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "./ui/button";
import { ArrowDown } from "lucide-react";
import { KineticHeading } from "./motion";
import { useConversion } from "../context/BookingContext";
import { HERO_IMAGE } from "../data";

export default function Hero() {
  const ref = useRef(null);
  const { openBooking, openQuote } = useConversion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.85]);

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative h-[100svh] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Detailed supercar under studio light" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-black/40" />

      <div className="relative z-10 h-full max-w-[1600px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 1 }}
          className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-zinc-300 mb-5"
        >
          Adelaide, South Australia · Cars · Bikes · Boats
        </motion.p>

        <h1 className="font-display font-extrabold uppercase leading-[0.85] tracking-tighter text-white text-[12.5vw] md:text-[10vw] lg:text-[8.5rem]">
          <KineticHeading lines={["A finish"]} />
          <KineticHeading lines={["worth"]} delay={0.1} className="block text-stroke-red" />
          <KineticHeading lines={["protecting."]} delay={0.2} />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center"
        >
          <Button
            data-testid="cta-book-service-hero"
            onClick={() => openBooking()}
            className="rounded-none bg-white text-black hover:bg-zinc-200 font-display uppercase tracking-widest h-14 px-9 text-sm"
          >
            Book a Service
          </Button>
          <Button
            data-testid="cta-get-quote-hero"
            onClick={() => openQuote()}
            className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] text-white font-display uppercase tracking-widest h-14 px-9 text-sm"
          >
            Get a Coating Quote
          </Button>
          <p className="text-zinc-400 text-sm sm:ml-4 max-w-xs">Instant deposit booking, or upload photos for a custom ceramic & correction quote.</p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-5 md:right-10 z-10 hidden md:flex items-center gap-2 text-zinc-400 text-xs tracking-widest uppercase"
      >
        <ArrowDown size={14} className="animate-bounce" /> Scroll
      </motion.div>
    </section>
  );
}
